import api from "./api";
import {
  GetCaixaDto,
  CaixaHistoricoDto,
  AbrirCaixaDto,
  CaixaAberturaDto,
  FecharCaixaDto,
  ApiResponse,
} from "../shared/dtos/Caixa.dto";

class CaixaService {
  async getFluxoCaixa(dataInicio?: string, dataFim?: string): Promise<GetCaixaDto[]> {
    try {
      let url = "/caixa/fluxo";
      if (dataInicio && dataFim) {
        url += `?dataInicio=${dataInicio}&dataFim=${dataFim}`;
      }
      const response = await api.get<GetCaixaDto[]>(url);
      return response.data;
    } catch (error: any) {
      console.error("Erro ao buscar fluxo de caixa:", error);
      if (error.response) {
        console.error("Detalhes do erro getFluxoCaixa:", {
          status: error.response.status,
          data: error.response.data,
        });
      }
      throw error;
    }
  }

  async getCaixaById(id: number): Promise<GetCaixaDto> {
    try {
      const response = await api.get<GetCaixaDto>(`/caixa/${id}`);
      return response.data;
    } catch (error: any) {
      console.error("Erro ao buscar caixa por ID:", error);
      if (error.response) {
        console.error("Detalhes do erro getCaixaById:", {
          status: error.response.status,
          data: error.response.data,
        });
      }
      throw error;
    }
  }

  async abrirCaixa(dados: AbrirCaixaDto): Promise<CaixaHistoricoDto> {
    try {
      const response = await api.post<CaixaHistoricoDto>("/caixa/abrir", dados);

      if (!response.data) {
        throw new Error("Resposta vazia do servidor ao abrir caixa");
      }

      return response.data;
    } catch (error: any) {
      console.error("Erro ao abrir caixa:", error);
      throw new Error(error.message || "Erro ao abrir caixa");
    }
  }

  async getCaixaAtual(): Promise<CaixaAberturaDto | null> {
    try {
      const response = await api.get<CaixaAberturaDto>("/caixa/atual");
      return response.data;
    } catch (error: any) {
      console.error("Erro ao buscar caixa atual:", error);
      if (error.response) {
        console.error("Detalhes do erro getCaixaAtual:", {
          status: error.response.status,
          data: error.response.data,
        });
      }
      throw error;
    }
  }

  async findCaixaOpen(): Promise<CaixaAberturaDto | null> {
    try {
      const response = await api.get<CaixaAberturaDto>("/caixa/find-caixa-open");
      return response.data;
    } catch (error: any) {
      console.error("Erro ao buscar caixa atual:", error);
      if (error.response) {
        console.error("Detalhes do erro getCaixaAtual:", {
          status: error.response.status,
          data: error.response.data,
        });
      }
      throw error;
    }
  }

  async fecharCaixa(
    id: number,
    valorFechamento: number,
    idUserFechamento: number
  ): Promise<CaixaHistoricoDto> {
    try {

      const fechamentoDto: FecharCaixaDto = {
        valorFechamento: valorFechamento,
        idUserFechamento: idUserFechamento,
      };

      const response = await api.post<CaixaHistoricoDto>(`/caixa/${id}/fechar`, fechamentoDto);
      return response.data;
    } catch (error: any) {
      console.error("Erro ao fechar caixa:", error);
      throw error;
    }
  }

  async getHistorico(): Promise<CaixaHistoricoDto[]> {
    try {
      const response = await api.get<ApiResponse>("/caixa/historico");

      if (!response.data || !response.data.data) {
        throw new Error("Resposta vazia do servidor");
      }

      return response.data.data;
    } catch (error: any) {
      console.error("Erro ao buscar histórico:", error);
      if (error.response) {
        console.error("Detalhes do erro:", {
          status: error.response.status,
          data: error.response.data,
        });
      }
      throw error;
    }
  }

  async getHistoricoByPage(page: number, limit: number): Promise<ApiResponse> {
    try {
      const response = await api.get<ApiResponse>(`/caixa/historico-by-page/${page}/${limit}`);

      if (!response.data) {
        throw new Error("Resposta vazia do servidor");
      }
      return response.data;
    } catch (error: any) {
      console.error("Erro ao buscar histórico por página:", error);
      if (error.response) {
        console.error("Detalhes do erro:", {
          status: error.response.status,
          data: error.response.data,
        });
      }
      throw error;
    }
  }

  async getHistoricoPorData(data: string): Promise<ApiResponse> {
    try {
      const response = await api.get<ApiResponse>("/caixa/historico", {
        params: { data },
      });
      return response.data;
    } catch (error: any) {
      console.error("Erro ao buscar histórico por data:", error);
      throw error;
    }
  }

  async getHistoricoPeriodo(
    dataInicio: string,
    dataFinal: string,
    page: number,
    limit: number
  ): Promise<{
    success: boolean;
    message: string;
    data: CaixaHistoricoDto[];
    total: number;
    page: number;
    take: number;
  }> {
    try {

      const response = await api.put<{
        success: boolean;
        message: string;
        data: CaixaHistoricoDto[];
        total: number;
        page: number;
        take: number;
      }>(
        `/caixa/periodo`,
        {
          dataInicio,
          dataFim: dataFinal,
        },
        {
          params: {
            page,
            take: limit,
          },
        }
      );

      if (!response.data) {
        throw new Error("Resposta vazia do servidor");
      }

      return response.data;
    } catch (error: any) {
      console.error("Erro ao buscar histórico por período:", error);
      if (error.response) {
        console.error("Detalhes do erro:", {
          status: error.response.status,
          data: error.response.data,
        });
      }
      throw error;
    }
  }

  async verificarEstadoCaixa(): Promise<{
    temCaixaAberto: boolean;
    caixaAberto?: CaixaHistoricoDto;
  }> {
    try {
      const historico = await this.getHistorico();

      if (!historico || historico.length === 0) {
        return { temCaixaAberto: false };
      }

      let caixaAberto;

      //Pega o caixa aberto com base na data atual
      caixaAberto = historico.find((caixa) => !caixa.dataFechamento);

      //Caso não tenha um caixa na data atual, verifica se existe um caixa aberto sem data de fechamento
      if (!caixaAberto) {
        caixaAberto = historico.find((caixa) => !caixa.dataFechamento);
      }

      return {
        temCaixaAberto: !!caixaAberto,
        caixaAberto: caixaAberto,
      };
    } catch (error) {
      console.error("Erro ao verificar estado do caixa:", error);
      return { temCaixaAberto: false };
    }
  }

  async verificarCaixaDiaAnteriorAberto(): Promise<{
    existeCaixaAnteriorAberto: boolean;
    caixaAnterior?: CaixaHistoricoDto;
  }> {
    try {
      const historico = await this.getHistorico();

      if (!historico || historico.length === 0) {
        return { existeCaixaAnteriorAberto: false };
      }

      // Filtra apenas caixas sem data de fechamento
      const caixasAbertos = historico.filter((caixa) => !caixa.dataFechamento);

      if (caixasAbertos.length === 0) {
        return { existeCaixaAnteriorAberto: false };
      }

      // Verifica a data atual
      const hoje = new Date();
      hoje.setHours(0, 0, 0, 0); // Zera horas, minutos, segundos e milissegundos para comparar apenas datas

      // Verifica se há algum caixa aberto de dias anteriores
      const caixaAnterior = caixasAbertos.find((caixa) => {
        const dataCaixa = new Date(caixa.dataCaixa);
        dataCaixa.setHours(0, 0, 0, 0);
        return dataCaixa.getTime() < hoje.getTime();
      });

      return {
        existeCaixaAnteriorAberto: !!caixaAnterior,
        caixaAnterior: caixaAnterior,
      };
    } catch (error) {
      console.error("Erro ao verificar caixa do dia anterior:", error);
      return { existeCaixaAnteriorAberto: false };
    }
  }
}

const caixaService = new CaixaService();
export default caixaService;
